import { Component, Input, Output, EventEmitter, forwardRef, OnDestroy, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil} from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { url_servicios } from 'src/app/config/config';
import { ApiV2Response, ListResponse } from '../../../core/models';

export interface LazySelectOption {
  value: unknown;
  label: string;
}

@Component({
  selector: 'app-lazy-select',
  templateUrl: './lazy-select.component.html',
  styleUrls: ['./lazy-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LazySelectComponent),
      multi: true
    }
  ]
})
export class LazySelectComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  @Input() placeholder: string = 'Select an option';
  @Input() noOptionsText: string = 'No options found';
  @Input() loadingText: string = 'Loading...';
  @Input() searchPlaceholder: string = 'Search...';
  @Input() pageSize: number = 10;
  @Input() clearable: boolean = false;
  @Input() url!: string;
  @Input() extraParams: Record<string, string | number | boolean> = {};
  @Input() optionValue: string = 'id';
  @Input() optionLabel!: string;
  @Input() includeSearch: number =  -1;
  @Input() multiple: boolean = false
  @Input() patient: unknown;
  @Input() initialOption?: LazySelectOption;

  @Output() searchChange = new EventEmitter<string>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() selectionChange = new EventEmitter<{ value: any }>();
  @Output() fieldBlur = new EventEmitter<FocusEvent>();
  @Output() closed = new EventEmitter<void>();
  @Output() clearSelections = new EventEmitter<void>();

  searchControl = new FormControl('');
  selectionControl = new FormControl();

  readonly destroy$ = new Subject<void>();

  options: LazySelectOption[] = [];

  selectedOptions: LazySelectOption[] = [];

  displayOptions: LazySelectOption[] = [];

  isLoadingSelect = false;
  hasMore = true;
  currentPage = 1;
  _optionLabels:string[] = [];

  
  // ControlValueAccessor implementation
  onChange: (value: unknown) => void = () => {};
  onTouched: () => void = () => {};
  disabled = false;
  value: unknown;
  
  readonly http = inject(HttpClient);

  ngOnInit(): void {
    // Pre-calcular las claves de label antes de cargar datos para evitar labels vacíos en la primera carga
    if (this.optionLabel.includes(',')) {
      this.optionLabel.split(',').forEach((item) => {
        this._optionLabels.push(item);
      });
    } else {
      this._optionLabels.push(this.optionLabel);
    }

    // Si hay una opción inicial, agregarla antes de la primera carga
    if (this.initialOption) {
      this.options = [this.initialOption];
      this.displayOptions = [this.initialOption];
    }

    // Initial fetch
    this.fetchOptions('', 1);

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.searchChange.emit(searchTerm || '');
        this.fetchOptions(searchTerm || '', 1);
      });

    this.selectionControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.updateSelectedOptions(value);
        this.onChange(value);
        this.onTouched();
        this.selectionChange.emit({ value });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patient'] && changes['patient'].currentValue !== undefined) {
      this.writeValue(changes['patient'].currentValue);
    }
  }

  updateSelectedOptions(selectedValues: unknown): void {
    if (!this.multiple || !Array.isArray(selectedValues)) {
      this.selectedOptions = [];
      return;
  }

  this.selectedOptions = selectedValues.map((value: unknown) => {
    // Buscar en displayOptions (que incluye tanto actuales como seleccionadas)
    let option = this.displayOptions.find(opt => opt.value === value);
    
    // Si no está en displayOptions, buscar en options originales
    if (!option) {
      option = this.options.find(opt => opt.value === value);
    }
    
    // Si no está en opciones ya seleccionadas
    if (!option) {
      option = this.selectedOptions.find(opt => opt.value === value);
    }
    
    return option || { value, label: `Loading... (${value})` };
  });

  this.updateDisplayOptions();
}

  updateDisplayOptions(): void {
    if (!this.multiple) {
      this.displayOptions = this.options;
      return;
    }

    // Combinar opciones seleccionadas con opciones actuales, evitando duplicados
    const optionMap = new Map<unknown, LazySelectOption>();
    
    // Agregar opciones seleccionadas primero (para que aparezcan arriba)
    this.selectedOptions.forEach(option => {
      optionMap.set(option.value, option);
    });
    
    // Agregar opciones actuales
    this.options.forEach(option => {
      optionMap.set(option.value, option);
    });
    
    this.displayOptions = Array.from(optionMap.values());
  }

fetchOptions(search: string = '', page: number = 1) {
    if (!this.url) return;

    this.isLoadingSelect = true;
      if (page === 1) {
      this.currentPage = 1;
    }

    let params = new HttpParams()
      .set('search', search)
      .set('page', page)
      .set('per_page', this.pageSize);

    // Append caller-provided extra params (e.g., role filters for users)
    Object.entries(this.extraParams || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      params = params.set(key, String(value));
    });

    if(this.includeSearch !== -1){
      params = params.set('include_search', this.includeSearch);
    }

    this.http.get<ApiV2Response<ListResponse<Record<string, unknown>>>>(`${url_servicios}${this.url}`, { params })
      .subscribe({
        next: (res) => {
          const newOptions = res.data.data.map((item) => this.transformData(item));

          if (page === 1) {
            // Si hay una opción inicial, preservarla y evitar duplicados
            const optionMap = new Map<unknown, LazySelectOption>();

            if (this.initialOption) {
              optionMap.set(this.initialOption.value, this.initialOption);
            }

            // Preservar la opción actualmente seleccionada para evitar que desaparezca tras el primer fetch
            const currentValue = this.selectionControl.value;
            if (currentValue !== null && currentValue !== undefined) {
              const selectedOption = this.options.find(opt => opt.value === currentValue) || this.selectedOptions.find(opt => opt.value === currentValue);
              if (selectedOption) {
                optionMap.set(selectedOption.value, selectedOption);
              }
            }

            newOptions.forEach(opt => optionMap.set(opt.value, opt));
            this.options = Array.from(optionMap.values());
          } else {
              this.options = [...this.options, ...newOptions];
          }

          const currentPage = res.data.current_page ?? page;
          this.currentPage = currentPage;
          this.hasMore = currentPage < res.data.last_page;
          this.isLoadingSelect = false;

          this.updateDisplayOptions();
        },
        error: () => {
          this.isLoadingSelect = false;
          if (page > 1) {
            this.currentPage--;
          }
        }
      });
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  writeValue(value: unknown): void {
    if (value === undefined) {
      return;
    }

    if (this.multiple) {
      const valuesArray = Array.isArray(value)
        ? value
        : value === null || value === undefined
        ? []
        : [value];

      this.selectionControl.setValue(valuesArray, { emitEvent: false });

      if (valuesArray.length === 0) {
        this.updateSelectedOptions(valuesArray);
        return;
      }

      const missingValues = valuesArray.filter((val) => {
        return (
          !this.options.some(opt => opt.value === val) &&
          !this.selectedOptions.some(opt => opt.value === val)
        );
      });

      if (missingValues.length > 0) {
        Promise.all(missingValues.map((id) => this.fetchById(id)))
          .then(fetchedOptions => {
            const newOptions = fetchedOptions.filter(Boolean);
            if (newOptions.length > 0) {
              const existing = new Set(this.options.map(opt => opt.value));
              this.options = [
                ...newOptions.filter(option => !existing.has(option.value)),
                ...this.options,
              ];
              this.updateDisplayOptions();
            }
          })
          .catch(() => {})
          .finally(() => {
            this.updateSelectedOptions(valuesArray);
          });
      } else {
        this.updateSelectedOptions(valuesArray);
      }
      return;
    }

    if (value === null) {
      this.selectionControl.setValue(null, { emitEvent: false });
      this.updateSelectedOptions(null);
      return;
    }

    if (
      typeof value === 'object' &&
      value !== null &&
      'value' in value &&
      'label' in value
    ) {
      const selectedOptionValue = (value as LazySelectOption).value;
      this.selectionControl.setValue(selectedOptionValue, { emitEvent: false });
      this.updateSelectedOptions(selectedOptionValue);
      return;
    }

    // Check if the option already exists in the options array before fetching
    const existingOption = this.options.find(opt => opt.value === value);
    if (existingOption) {
      this.selectionControl.setValue(existingOption.value, { emitEvent: false });
      this.updateSelectedOptions(existingOption.value);
      return;
    }

    this.fetchById(value).then((option) => {
      this.selectionControl.setValue(option.value, { emitEvent: false });
      this.updateSelectedOptions(option.value);
      this.options = [option, ...this.options];
      this.updateDisplayOptions();
    });
  }

  // Nuevo método para buscar por ID
  async fetchById(id: unknown): Promise<LazySelectOption> {
    // Strip query params to avoid malformed /path?param=value/123 requests
    const baseUrl = this.url.split('?')[0];
    const res = await this.http
      .get<{ data: Record<string, unknown> }>(`${url_servicios}${baseUrl}/${id}`)
      .toPromise();
    return this.transformData(res?.data ?? {});
  }
  
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }
  
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  
   setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.searchControl.disable();
      this.selectionControl.disable();
    } else {
      this.searchControl.enable();
      this.selectionControl.enable();
    }
  }
  
  onRequestMoreData():void{
    if (this.hasMore && !this.isLoadingSelect) {
      this.currentPage++;
      this.fetchOptions(this.searchControl.value || '', this.currentPage);
    }
  }
  trackByFn(index: number, item: LazySelectOption): unknown {
    void index;
    return item.value;
  }

  clearSelection(): void {
    this.selectionControl.setValue(null); // Borra selección
    this.searchControl.setValue(''); // Limpia campo de búsqueda
    this.searchChange.emit(''); // Emite búsqueda vacía
    this.selectedOptions = [];
    this.updateDisplayOptions();
    this.clearSelections.emit(); // Emite evento de limpieza  
  }

  transformData(item: Record<string, unknown>): LazySelectOption {
    let labels = '';
    this._optionLabels.forEach((val) => {
      const rawValue = item[val];
      labels += `${typeof rawValue === 'string' || typeof rawValue === 'number' ? rawValue : ''} `;
    });
    
    return { 
            value: item[this.optionValue], 
            label: labels
          }
  }

  onClosed(): void {
    this.closed.emit();
  }

  onBlur(event: FocusEvent): void {
    this.onTouched();
    this.fieldBlur.emit(event);
  }
}
